import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { calendar, moduleColors } from '../data/studyData';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay,
  addMonths,
  subMonths,
  parseISO
} from 'date-fns';
import { ChevronLeft, ChevronRight, Video } from 'lucide-react';

export const MonthlyView = ({ selectedDate, onDateSelect, currentMonth, onMonthChange, completedTasks }) => {
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
    const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    return eachDayOfInterval({ start: calStart, end: calEnd });
  }, [currentMonth]);

  const getDayData = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendar.find(d => d.date === dateStr);
  };

  const isToday = (date) => {
    return isSameDay(date, new Date());
  };

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <Card 
      className="rounded-2xl border-2 shadow-sm"
      data-testid="monthly-view"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-2xl">
            {format(currentMonth, 'MMMM yyyy')}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onMonthChange(subMonths(currentMonth, 1))}
              data-testid="prev-month-btn"
              className="rounded-xl border-2"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => onMonthChange(new Date())}
              data-testid="today-btn"
              className="rounded-xl border-2 px-4"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onMonthChange(addMonths(currentMonth, 1))}
              data-testid="next-month-btn"
              className="rounded-xl border-2"
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Week Day Headers */}
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day) => (
            <div 
              key={day} 
              className="text-center text-sm font-semibold text-muted-foreground py-2"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((date, index) => {
            const dayData = getDayData(date);
            const isCurrentMonth = isSameMonth(date, currentMonth);
            const isSelected = isSameDay(date, selectedDate);
            const isTodayDate = isToday(date);
            const dateStr = format(date, 'yyyy-MM-dd');
            const completedForDay = completedTasks[dateStr] || [];
            const allTasksCompleted = dayData && dayData.tasks.length > 0 && 
              completedForDay.length === dayData.tasks.length;

            const moduleStyle = dayData ? (moduleColors[dayData.module] || moduleColors.Rest) : null;

            return (
              <button
                key={index}
                onClick={() => onDateSelect(date)}
                disabled={!isCurrentMonth}
                data-testid={`calendar-day-${format(date, 'yyyy-MM-dd')}`}
                className={`
                  calendar-day-cell relative p-2 min-h-[80px] rounded-xl border-2 
                  text-left transition-all duration-150
                  ${!isCurrentMonth ? 'opacity-30 cursor-not-allowed bg-muted/20' : 'cursor-pointer hover:shadow-md'}
                  ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
                  ${isTodayDate && isCurrentMonth ? 'border-primary' : 'border-border'}
                  ${dayData && isCurrentMonth ? moduleStyle.bg : 'bg-card'}
                  ${dayData?.isRetreat ? 'retreat-day' : ''}
                `}
                aria-label={`${format(date, 'MMMM d, yyyy')}${dayData ? `, ${dayData.module}` : ''}`}
              >
                {/* Day Number */}
                <div className={`
                  flex items-center justify-between mb-1
                `}>
                  <span className={`
                    text-sm font-semibold
                    ${isTodayDate && isCurrentMonth ? 'bg-primary text-primary-foreground w-7 h-7 rounded-full flex items-center justify-center' : ''}
                    ${dayData && isCurrentMonth ? moduleStyle.text : 'text-foreground'}
                  `}>
                    {format(date, 'd')}
                  </span>
                  
                  {/* Tutorial indicator */}
                  {dayData?.tutorials?.length > 0 && (
                    <Video className="w-3 h-3 text-amber-600" />
                  )}
                </div>

                {/* Module & Hours */}
                {dayData && isCurrentMonth && (
                  <div className="space-y-1">
                    {dayData.study_hours > 0 && (
                      <p className={`text-xs font-medium ${moduleStyle.text} opacity-80`}>
                        {dayData.study_hours}h
                      </p>
                    )}
                    
                    {/* Task completion indicator */}
                    {dayData.tasks.length > 0 && (
                      <div className="flex items-center gap-1">
                        <div className={`
                          w-full h-1 rounded-full overflow-hidden
                          ${allTasksCompleted ? 'bg-emerald-500' : 'bg-muted-foreground/20'}
                        `}>
                          <div 
                            className="h-full bg-emerald-500 transition-all duration-300"
                            style={{ width: `${(completedForDay.length / dayData.tasks.length) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Module Indicator Bar */}
                {dayData && isCurrentMonth && (
                  <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-xl ${moduleStyle.indicator}`} />
                )}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
